import { css } from '@emotion/react';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import cms_api from '../../cms_api';
import { connectLocation } from '../location/LocationConnectors';

export const getBadgeData = _.memoize(async code => {
  const [response, response_en] = await Promise.all([
    cms_api.get_single_contents({
      filter: {
        type: 'Cms::Content::Attribute',
        language_iso_code: code,
        route: '/roofing/contractors',
        name: 'FAC Badges',
      },
      fields: {
        '*': 'contents',
      },
    }),
    cms_api.get_single_contents({
      filter: {
        type: 'Cms::Content::Attribute',
        language_iso_code: 'en-US',
        route: '/roofing/contractors',
        name: 'FAC Badges',
      },
      fields: {
        '*': 'contents',
      },
    }),
  ]);
  return response?.items.map(item => ({
    ...item,
    analyticsName: response_en?.items?.filter(item2 => item2.value == item.value)?.[0]?.label || item.label,
  }));
});

const ContractorBadge = ({ openModal, modalKey, imgUrl, altText, dataTrackDestination, label, isMobile }) => (
  <li className={isMobile ? 'mobile-badge-with-text' : ''}>
    <a
      onClick={() => openModal(modalKey)}
      tabIndex="0"
      data-track="tooltip"
      data-track-destination={dataTrackDestination}
      onKeyPress={e => {
        if (e.key === 'Enter') openModal(modalKey);
      }}
    >
      <img src={imgUrl} alt={altText} />
      {isMobile && label && <span className="badge-text">{label}</span>}
    </a>
  </li>
);

const AWARD_WINNER = 'AWARD_WINNER';
function singularizeBadgeKey(badge) {
  if (Array.isArray(badge.value)) {
    // CEEBB is a platinum award. we'll use that code to determine if this is the platinum award badge
    if (badge.value.includes('CEEBB')) {
      return AWARD_WINNER;
    }
    console.warn('unexpected multi-value badge, returning first value', badge);
    return badge.value[0];
  }
  return badge.value;
}

export const ContractorBadges = connectLocation(({ t, locale: { code }, active_certifications, ...props }) => {
  const [badgeData, setBadgeData] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    getBadgeData(code).then(data => setBadgeData(data));
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const activeBadges = active_certifications || [];

  const mobileBadgePriority = ['FIN', AWARD_WINNER, 'RECYCLER', 'SOLAR'];
  const filteredBadges = badgeData.filter(
    badge => activeBadges.filter(value => Array.wrap(badge.value || []).includes(value)).length > 0
  );

  const badgesToShow = isMobile
    ? filteredBadges
        .filter(badge => mobileBadgePriority.includes(singularizeBadgeKey(badge)))
        .sort((a, b) => {
          const getPriorityIndex = badge => {
            const index = mobileBadgePriority.indexOf(singularizeBadgeKey(badge));
            if (index !== -1) return index;
            return 999;
          };

          const aIndex = getPriorityIndex(a);
          const bIndex = getPriorityIndex(b);
          return aIndex - bIndex;
        })
        .slice(0, 4)
    : filteredBadges;

  const badgeWrapperStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  `;

  return (
    <div css={badgeWrapperStyles}>
      {badgesToShow.map(badge => (
        <ContractorBadge
          key={badge.value}
          modalKey={badge.value}
          imgUrl={badge.icon?.file}
          altText={badge.icon?.alt}
          dataTrackDestination={badge.analyticsName}
          label={badge.label}
          isMobile={isMobile}
          openModal={props.openModal}
        />
      ))}
    </div>
  );
});
